import { useAccess } from "../access/api";
import type { MyAccess } from "../access/api";
import { findEntry } from "../routes/switchboard";
import type { SwitchEntry } from "../routes/switchboard";

/** Which Map the switchboard serves: the new page ("next") or today's ("legacy"). */
export type MapMode = SwitchEntry["mode"];
export const mapMode = (board?: SwitchEntry[]): MapMode => findEntry("/map", board)?.entry.mode ?? "legacy";

/**
 * The one rule for every Map entry point (menu, Plan a route, Show on the map, Review on the map). It replaces pre-flight I8:
 *  - the new Map is served: anyone who plans routes (Plan routes) may open it, whatever their reach (it shows only what is in reach);
 *  - today's Map is served: Plan routes AND reach over everything, because today's Map cannot hide the names of routes outside reach.
 * `mode` defaults to what the switchboard serves now, so flipping the line flips every entry point with it.
 */
export const mapOpens = (plan: boolean, reachAll: boolean, mode: MapMode = mapMode()): boolean => plan && (mode === "next" || reachAll);

export const canOpenMap = (access: Pick<MyAccess, "permissions" | "reach"> | null | undefined, mode: MapMode = mapMode()): boolean =>
  !!access && mapOpens(!!access.permissions?.includes("routes.plan"), !!access.reach?.all, mode);

/** The same rule for a component. False while the answer is loading or when its last refresh failed (links appear only when allowed). */
export const useCanOpenMap = (): boolean => {
  const { data, error } = useAccess();
  return !error && canOpenMap(data);
};

/** "Review on the map" opens at most this many routes (the address stays short, and the legend readable). */
export const MAX_REVIEW_ROUTES = 12;

export const mapLink = {
  pick: () => "/map?tool=pick",
  /** A route (or several) opens in its own project: the address carries it, so a route outside the person's current project shows with its ticks. */
  route: (id: number, projectId?: number | null) => `/map?route=${id}${projectId ? `&project=${projectId}` : ""}`,
  routes: (ids: number[], projectId?: number | null) => `/map?routes=${ids.join(",")}${projectId ? `&project=${projectId}` : ""}`,
  contact: (id: number) => `/map?contact=${id}`,
};
